import { Type, Download, Calendar, ArrowRight } from 'lucide-react';
import { ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useRecordContext } from 'react-admin';
import { useNotify } from 'ra-core';
import type { Creative } from '../offerDrawer/creatives/types';
import type { Offer } from '../types';
import { downloadFile } from '../../../utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DISPLAY_TIME_ZONE } from '../../../constants';

const fmtShortDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });

type Props = {
  creative: Creative;
  onViewDetails: () => void;
};

function OfferCreativeCard({ creative, onViewDetails }: Props) {
  const offer = useRecordContext<Offer>();
  const notify = useNotify();
  const imageAssets = creative.creative_assets.filter(
    a => a.asset_type === 'image' && a.image
  );
  const heroUrl = imageAssets[0]?.image?.preview_url;
  const brandLogo = offer?.brand?.logo;
  const brandName = offer?.brand?.name ?? '';

  const handleDownloadAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const results = await Promise.all(
      imageAssets.map(a =>
        downloadFile(a.image!.preview_url, a.image!.original_filename || a.name)
      )
    );
    if (results.some(ok => !ok)) {
      notify('Some images failed to download', { type: 'error' });
    }
  };

  return (
    <div
      onClick={onViewDetails}
      className="group border border-border rounded-xl overflow-hidden flex flex-col h-full shadow-premium text-left w-full transition-[box-shadow,border-color] hover:shadow-premium-hover hover:border-border-hover cursor-pointer"
    >
      {/* Hero */}
      <div className="relative w-full aspect-[1.89/1] bg-muted shrink-0">
        {heroUrl ? (
          <img
            src={heroUrl}
            alt={creative.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={brandName}
                className="size-12 object-contain opacity-30"
              />
            ) : (
              <Type
                className="size-10 text-muted-foreground/30"
                strokeWidth={1.25}
              />
            )}
          </div>
        )}

        {/* Time constraint badge */}
        {(creative.start_date || creative.end_date) && (
          <div className="absolute inset-x-0 bottom-8 flex justify-center z-20">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 h-6 px-2 py-1 bg-amber-500/90 border border-amber-400/60 backdrop-blur-2xl rounded-sm text-white text-xs font-medium cursor-default">
                  <Calendar className="size-3.5 shrink-0" strokeWidth={2.5} />
                  {creative.start_date && fmtShortDate(creative.start_date)}
                  {creative.start_date && creative.end_date && (
                    <ArrowRight className="size-3 shrink-0" strokeWidth={2.5} />
                  )}
                  {creative.end_date && fmtShortDate(creative.end_date)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                This creative must run exclusively during that time window.
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Brand logo floats over the bottom edge of the hero */}
        {heroUrl && brandLogo && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 size-[50px] rounded-[7px] border border-border bg-white shadow-md flex items-center justify-center z-10 overflow-hidden">
            <img
              src={brandLogo}
              alt={brandName}
              className="w-full h-full object-contain"
            />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="bg-white text-black flex flex-col items-stretch px-3 pt-10 pb-3 gap-3 flex-1">
        <div className="flex flex-col items-center gap-1 w-full text-center">
          <p className="text-base font-semibold leading-6 truncate w-full">
            {creative.name}
          </p>
        </div>

        <div className="flex gap-2 mt-auto">
          <div className="flex-1 bg-[#15171A] group-hover:bg-[#24282c] text-white rounded-md h-10 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors">
            <ListMagnifyingGlassIcon className="size-4" />
            View Details
          </div>
          {imageAssets.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleDownloadAll}
                  className="size-10 shrink-0 bg-[#15171A] hover:bg-[#24282c] text-white rounded-md flex items-center justify-center transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Download className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Download all images</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

export default OfferCreativeCard;
